import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, withCtx as _withCtx, createBlock as _createBlock, resolveDynamicComponent as _resolveDynamicComponent, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode } from "vue"

const _hoisted_1 = { class: "app-shell" }
const _hoisted_2 = {
  key: 0,
  class: "app-boot",
  role: "status",
  "aria-label": "화면을 준비하고 있습니다"
}
const _hoisted_3 = {
  key: 0,
  href: "#main-content",
  class: "skip-link"
}

import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRoute, useRouter, type RouteLocationNormalizedLoaded } from 'vue-router'
import PortalHeader from '@/components/layout/PortalHeader.vue'
import PortalFooter from '@/components/layout/PortalFooter.vue'
import SectionFrame from '@/components/layout/SectionFrame.vue'
import ScrollToTop from '@/components/layout/ScrollToTop.vue'
import MobileBottomNav from '@/components/layout/MobileBottomNav.vue'
import MallHeader from '@/components/mall/MallHeader.vue'
import MallBottomNav from '@/components/mall/MallBottomNav.vue'
import MallToastHost from '@/components/mall/MallToastHost.vue'
import ConfirmDialog from '@/components/common/ConfirmDialog.vue'
import SellerLayout from '@/components/seller/SellerLayout.vue'
import AdminLayout from '@/components/admin/AdminLayout.vue'
import { useMallToast } from '@/composables/useMallToast'
import { useMemberAuth } from '@/composables/useMemberAuth'
import { saveRecentVisit } from '@/composables/usePortalStorage'
import { recordPortalVisit, shouldRecordPortalVisit } from '@/api/portalVisit'
import { applyDocumentTitle } from '@/utils/documentTitle'
import { illust } from '@/constants/illust'
import { landingGateOpen, landingTransit } from '@/composables/useOrgType'
import { preloadImages } from '@/utils/preloadImage'
import { glueKoreanTree } from '@/utils/glueKoreanLine'
import { portalSectionForPath } from '@/constants/portalSections'


export default /*@__PURE__*/_defineComponent({
  __name: 'App',
  setup(__props) {

const route = useRoute()
const router = useRouter()
const appReady = ref(false)
const siteAppEl = ref<HTMLElement | null>(null)
const LAND_ROUTE_NAMES = new Set(['landing', 'wage', 'facility', 'visiting-care'])
const isLandHome = computed(() => LAND_ROUTE_NAMES.has(String(route.name ?? '')))
const isHome = computed(() => isLandHome.value)
const LANDING_SHELL_NAMES = new Set(['dashboard', 'terms', 'login', 'info-plaza', 'info-detail', 'info-center', 'info-story'])
const isPortalHome = computed(() => LANDING_SHELL_NAMES.has(String(route.name ?? '')))
const isGate = computed(() => isLandHome.value && (landingGateOpen.value || landingTransit.value))
const portalSection = computed(() => portalSectionForPath(route.path))

function landViewKey(viewRoute: RouteLocationNormalizedLoaded) {
  const name = String(viewRoute.name ?? '')
  if (name === 'info-center' || name === 'info-detail') return '/info'
  if (name === 'login' || name === 'info-plaza') return viewRoute.path
  if (LAND_ROUTE_NAMES.has(name)) return 'land-home'
  return viewRoute.fullPath
}

const layoutOf = (name: string) =>
  route.matched.some((record) => record.meta.layout === name) || route.meta.layout === name
/** 함우리몰 라우트는 본 사이트 헤더·푸터를 몰 전용으로 통째로 교체한다. */
const isMall = computed(() => layoutOf('mall'))
/** 판매관리센터는 관리 화면이라 레이아웃 자체가 다르다. */
const isSeller = computed(() => layoutOf('seller'))
/** 관리자 화면 */
const isAdmin = computed(() => layoutOf('admin'))
/** 관리자 로그인처럼 셸 없이 혼자 뜨는 화면 */
const isBare = computed(() => layoutOf('bare'))

const { initMemberAuth } = useMemberAuth()
const { showError } = useMallToast()

function onOffline() {
  showError('네트워크 연결이 끊겼습니다. 연결 후 다시 시도해 주세요.')
}

let glueFrame = 0
let glueObserver: MutationObserver | null = null
function scheduleGlue() {
  if (isMall.value || !siteAppEl.value) return
  cancelAnimationFrame(glueFrame)
  glueFrame = requestAnimationFrame(() => {
    if (!isMall.value && siteAppEl.value) glueKoreanTree(siteAppEl.value)
  })
}

onMounted(async () => {
  window.addEventListener('offline', onOffline)
  /* 라우터 가드가 세션을 끝낼 때까지 헤더가 먼저 빈 계정으로 판단하지 않게 한다. */
  await router.isReady()
  appReady.value = true
  await nextTick()
  initMemberAuth()
  if (!isMall.value && !isSeller.value && !isAdmin.value) {
    preloadImages([illust.wage, illust.accounting])
  }
  scheduleGlue()
  if (siteAppEl.value) {
    glueObserver = new MutationObserver(() => scheduleGlue())
    glueObserver.observe(siteAppEl.value, { childList: true, subtree: true, characterData: true })
  }
})

watch(() => route.fullPath, () => {
  void nextTick(() => scheduleGlue())
})

onUnmounted(() => {
  glueObserver?.disconnect()
  cancelAnimationFrame(glueFrame)
  window.removeEventListener('offline', onOffline)
})

router.afterEach((to) => {
  applyDocumentTitle(to)
  const label = typeof to.meta.visitLabel === 'string' ? to.meta.visitLabel : String(to.name ?? to.path)
  saveRecentVisit(label, to.fullPath)

  const layout = String(to.meta.layout || to.matched.find((record) => record.meta.layout)?.meta.layout || '')
  if (shouldRecordPortalVisit(layout)) {
    void recordPortalVisit(to.query as Record<string, unknown>)
  }
})

/* 첫 진입·새로고침 시에도 탭 이름을 맞춘다 */
applyDocumentTitle(route)

return (_ctx: any,_cache: any) => {
  const _component_router_view = _resolveComponent("router-view")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (!appReady.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2))
      : (isSeller.value)
        ? (_openBlock(), _createBlock(SellerLayout, { key: 1 }, {
            default: _withCtx(() => [
              _createVNode(_component_router_view)
            ]),
            _: 1
          }))
        : (isAdmin.value)
          ? (_openBlock(), _createBlock(AdminLayout, { key: 2 }, {
              default: _withCtx(() => [
                _createVNode(_component_router_view)
              ]),
              _: 1
            }))
          : (isBare.value)
            ? (_openBlock(), _createBlock(_component_router_view, { key: 3 }))
            : (_openBlock(), _createElementBlock("div", {
                key: 4,
                ref_key: "siteAppEl",
                ref: siteAppEl,
                class: _normalizeClass(["site-app", { 'site-app--mall': isMall.value, 'site-app--gate': isGate.value, 'site-app--landing': isPortalHome.value }])
              }, [
                (isMall.value)
                  ? (_openBlock(), _createElementBlock("a", _hoisted_3, "본문으로 건너뛰기"))
                  : _createCommentVNode("", true),
                (isMall.value)
                  ? (_openBlock(), _createBlock(MallHeader, { key: 1 }))
                  : (_openBlock(), _createBlock(PortalHeader, { key: 2 })),
                _createElementVNode("main", {
                  id: "main-content",
                  class: _normalizeClass(["site-main", { 'site-main--home': isHome.value }]),
                  tabindex: "-1"
                }, [
                  (isMall.value)
                    ? (_openBlock(), _createBlock(_component_router_view, { key: 0 }, {
                        default: _withCtx(({ Component, route: viewRoute }) => [
                          (_openBlock(), _createBlock(_resolveDynamicComponent(Component), {
                            key: viewRoute.fullPath
                          }))
                        ]),
                        _: 1
                      }))
                    : (portalSection.value && !isPortalHome.value)
                      ? (_openBlock(), _createBlock(SectionFrame, {
                          key: 1,
                          section: portalSection.value
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_component_router_view, null, {
                              default: _withCtx(({ Component, route: viewRoute }) => [
                                (_openBlock(), _createBlock(_resolveDynamicComponent(Component), {
                                  key: landViewKey(viewRoute)
                                }))
                              ]),
                              _: 1
                            })
                          ]),
                          _: 1
                        }, 8, ["section"]))
                      : (_openBlock(), _createBlock(_component_router_view, { key: 2 }, {
                          default: _withCtx(({ Component, route: viewRoute }) => [
                            (_openBlock(), _createBlock(_resolveDynamicComponent(Component), {
                              key: landViewKey(viewRoute)
                            }))
                          ]),
                          _: 1
                        }))
                ], 2),
                (!isGate.value)
                  ? (_openBlock(), _createBlock(PortalFooter, { key: 3 }))
                  : _createCommentVNode("", true),
                _createVNode(ScrollToTop),
                (isMall.value)
                  ? (_openBlock(), _createBlock(MallToastHost, { key: 4 }))
                  : _createCommentVNode("", true),
                (isMall.value)
                  ? (_openBlock(), _createBlock(MallBottomNav, { key: 5 }))
                  : (!isGate.value && !isPortalHome.value)
                    ? (_openBlock(), _createBlock(MobileBottomNav, { key: 6 }))
                    : _createCommentVNode("", true)
              ], 2)),
    _createVNode(ConfirmDialog)
  ]))
}
}

})